import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';

import type { ConnectionPhases, IResourceTimingProvider } from './i-resource-timing.provider';

/**
 * Quanto si aspetta la voce di una chiamata prima di rinunciare. Il browser la mette in coda quando la
 * risposta e' finita, cioe' subito dopo l'evento che fa registrare la riga: un quarto di secondo e'
 * molto piu' del necessario, e una riga del diario non deve restare appesa.
 */
const ENTRY_WAIT_MS = 250;

/**
 * Cio' che il browser ha misurato da se' su ogni chiamata (Resource Timing). E' l'unico punto che legge
 * `performance` per conto del diario tecnico.
 */
@Injectable()
export class PerformanceResourceTimingProvider implements IResourceTimingProvider {
  private readonly document = inject(DOCUMENT);

  async connectionPhasesFor(url: string, startedAt: number): Promise<ConnectionPhases | null> {
    const performance = this.document.defaultView?.performance;
    if (!performance) return null;

    const address = this.absolute(url);
    const since = startedAt - performance.timeOrigin;

    return (
      this.phasesOf(this.entry(performance, address, since)) ??
      this.phasesOf(await this.awaitedEntry(address, since))
    );
  }

  /** La voce gia' arrivata, se c'e': la piu' recente fra quelle cominciate dopo la chiamata. */
  private entry(
    performance: Performance,
    address: string,
    since: number,
  ): PerformanceResourceTiming | null {
    const entries = performance
      .getEntriesByName(address, 'resource')
      .filter((entry): entry is PerformanceResourceTiming => entry.startTime >= since - 1);

    return entries.at(-1) ?? null;
  }

  /**
   * La voce che deve ancora arrivare. Il browser la annuncia quando la risposta e' completa, che e' un
   * istante dopo l'evento con cui la chiamata si e' conclusa: si aspetta quell'istante e non di piu'.
   */
  private awaitedEntry(address: string, since: number): Promise<PerformanceResourceTiming | null> {
    const observerType = this.document.defaultView?.PerformanceObserver;
    if (!observerType) return Promise.resolve(null);

    return new Promise((resolve) => {
      const done = (entry: PerformanceResourceTiming | null): void => {
        clearTimeout(deadline);
        observer.disconnect();
        resolve(entry);
      };

      const observer = new observerType((list) => {
        const found = list
          .getEntries()
          .filter((entry): entry is PerformanceResourceTiming => entry.entryType === 'resource')
          .find((entry) => entry.name === address && entry.startTime >= since - 1);

        if (found) done(found);
      });

      const deadline = setTimeout(() => done(null), ENTRY_WAIT_MS);

      observer.observe({ type: 'resource', buffered: true });
    });
  }

  /**
   * Le fasi che la voce dichiara, o `null` quando non ne dichiara nessuna: su un'altra origine il
   * browser le azzera tutte finche' la risposta non porta `Timing-Allow-Origin`, e uno zero letto come
   * misura direbbe che la connessione e' stata istantanea invece che «non si puo' sapere».
   */
  private phasesOf(entry: PerformanceResourceTiming | null): ConnectionPhases | null {
    if (!entry || entry.requestStart === 0) return null;

    return {
      dnsMs: this.span(entry.domainLookupStart, entry.domainLookupEnd),
      tcpMs: this.span(entry.connectStart, entry.secureConnectionStart || entry.connectEnd),
      // `secureConnectionStart` e' zero su una connessione in chiaro: li' non c'e' nessuna stretta di mano.
      tlsMs: this.span(entry.secureConnectionStart, entry.connectEnd),
      ttfbMs: this.span(entry.requestStart, entry.responseStart),
      ...(entry.transferSize > 0 ? { wireBytes: entry.transferSize } : {}),
    };
  }

  /** Millisecondi interi: il diario e' testo da leggere, e i decimi di un DNS non decidono niente. */
  private span(from: number, to: number): number {
    return from > 0 && to > from ? Math.round(to - from) : 0;
  }

  /** L'indirizzo come lo scrive il Resource Timing: assoluto, anche quando la chiamata e' relativa. */
  private absolute(url: string): string {
    try {
      return new URL(url, this.document.baseURI).href;
    } catch {
      return url;
    }
  }
}
